// src/components/FullCPTCoderAPIDocs.js
import {
  Box, Grid, GridItem, Text, VStack, HStack, Link, Divider, Heading,
  Code, Container, Button, Spinner, Badge, Tabs, TabList, TabPanels,
  Tab, TabPanel, useColorModeValue, UnorderedList, ListItem, Table,
  Thead, Tbody, Tr, Th, Td, Accordion, AccordionItem, AccordionButton,
  AccordionPanel, AccordionIcon, OrderedList, Alert, AlertIcon, AlertTitle, AlertDescription
} from "@chakra-ui/react";
import { ExternalLinkIcon, InfoIcon } from '@chakra-ui/icons';
import HeaderMenus from './HeaderMenus';
import React from 'react';

function FullCPTCoderAPIDocs() {
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const codeBg = useColorModeValue("gray.50", "gray.700");
  const accentColor = useColorModeValue("blue.500", "blue.300");

  const EndpointSection = ({ id, title, method, endpoint, description, headers, pathParams, requestBody, responses, example }) => (
    <Box id={id} mb={8} p={4} borderWidth="1px" borderRadius="lg" borderColor={borderColor}>
      <HStack mb={4}>
        <Badge colorScheme={
          method === 'GET' ? 'green' :
          method === 'POST' ? 'blue' :
          method === 'PUT' ? 'orange' :
          method === 'DELETE' ? 'red' : 'gray'
        } fontSize="md" px={2} py={1}>{method}</Badge>
        <Heading as="h3" size="md">{title}</Heading>
      </HStack>

      <Text mb={4}>{description}</Text>

      <Box mb={4}>
        <Heading as="h4" size="sm" mb={2}>Endpoint</Heading>
        <Code p={2} borderRadius="md" bg={codeBg} children={endpoint} />
      </Box>

      {headers && (
        <Box mb={4}>
          <Heading as="h4" size="sm" mb={2}>Headers</Heading>
          <Code p={2} borderRadius="md" bg={codeBg} display="block" whiteSpace="pre">
            {headers}
          </Code>
        </Box>
      )}

      {pathParams && (
        <Box mb={4}>
          <Heading as="h4" size="sm" mb={2}>Path Parameters</Heading>
          <Table size="sm" variant="simple">
            <Thead>
              <Tr>
                <Th>Parameter</Th>
                <Th>Description</Th>
                <Th>Required</Th>
              </Tr>
            </Thead>
            <Tbody>
              {pathParams.map((param, index) => (
                <Tr key={index}>
                  <Td><Code>{param.name}</Code></Td>
                  <Td>{param.description}</Td>
                  <Td>{param.required ? "Yes" : "No"}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      {requestBody && (
        <Box mb={4}>
          <Heading as="h4" size="sm" mb={2}>Request Body</Heading>
          <Code p={2} borderRadius="md" bg={codeBg} display="block" whiteSpace="pre">
            {requestBody}
          </Code>
        </Box>
      )}

      {responses && (
        <Box mb={4}>
          <Heading as="h4" size="sm" mb={2}>Responses</Heading>
          <Accordion allowMultiple>
            {responses.map((response, index) => (
              <AccordionItem key={index}>
                <h2>
                  <AccordionButton>
                    <Box flex="1" textAlign="left">
                      <Badge colorScheme={
                        response.status >= 200 && response.status < 300 ? 'green' :
                        response.status >= 400 && response.status < 500 ? 'yellow' :
                        response.status >= 500 ? 'red' : 'gray'
                      }>{response.status}</Badge>
                      {' '}{response.description}
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                </h2>
                <AccordionPanel pb={4}>
                  <Code p={2} borderRadius="md" bg={codeBg} display="block" whiteSpace="pre">
                    {response.example}
                  </Code>
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>
        </Box>
      )}

      {example && (
        <Box mb={4}>
          <Heading as="h4" size="sm" mb={2}>Example Request</Heading>
          <Code p={2} borderRadius="md" bg={codeBg} display="block" whiteSpace="pre">
            {example}
          </Code>
        </Box>
      )}
    </Box>
  );

  return (
    <Box>
      <Grid
        templateAreas={`"header header"
                        "nav main"
                        "footer footer"`}
        gridTemplateRows={'60px 1fr 60px'}
        gridTemplateColumns={'300px 1fr'}
        h='100vh'
        gap='2'
        color='blackAlpha.800'
        fontWeight='bold'
      >
        {/* Header with menu */}
        <HeaderMenus />

        {/* Navigation sidebar */}
        <GridItem area="nav" bg={bgColor} p={4} overflowY="auto" borderRight="1px" borderColor={borderColor}>
          <VStack spacing={4} align="start" width="100%">
            <Heading as="h3" size="md" mb={2}>Medical Coding API</Heading>

            <VStack align="start" spacing={2} width="100%">
              <Link href="#overview" fontSize="md" fontWeight="semibold">Overview</Link>
              <Link href="#authentication" fontSize="md" fontWeight="semibold">Authentication</Link>
              <Link href="#workflow" fontSize="md" fontWeight="semibold">Coding Workflow</Link>

              <Heading as="h4" size="sm" mt={2}>Endpoints</Heading>
              <Link href="#create-case" fontSize="sm" pl={4}>1. Create Case</Link>
              <Link href="#assign-carrier" fontSize="sm" pl={4}>2. Assign Carrier (Optional)</Link>
              <Link href="#upload-file" fontSize="sm" pl={4}>3. Upload Content</Link>
              <Link href="#cpt-coding" fontSize="sm" pl={4}>4. Get CPT Codes</Link>
              <Link href="#icd10-coding" fontSize="sm" pl={4}>5. Get ICD-10 Codes</Link>

              <Heading as="h4" size="sm" mt={2}>Advanced</Heading>
              <Link href="#async-processing" fontSize="sm" pl={4}>Asynchronous Processing</Link>
            </VStack>
          </VStack>
        </GridItem>

        {/* Main content area */}
        <GridItem area="main" bg={bgColor} p={6} overflowY="auto">
          <Box maxW="1000px" mx="auto">
            {/* Overview Section */}
            <Box id="overview" mb={10}>
              <Heading as="h1" size="xl" mb={4}>Medical Coding API Documentation</Heading>
              <Text fontSize="lg" mb={4}>
                This document outlines the synchronous API workflow for submitting medical documentation and receiving CPT and ICD-10 coding results.
                This is the recommended path for server-to-server integrations or automated scripting.
              </Text>
              <Box p={4} borderLeft="4px" borderColor={accentColor} bg={codeBg} borderRadius="md">
                <Text fontWeight="bold">Base URL</Text>
                <Code>https://api.revedy.io</Code>
              </Box>
            </Box>

            {/* Authentication Section */}
            <Box id="authentication" mb={10}>
              <Heading as="h2" size="lg" mb={4}>Authentication</Heading>
              <Text mb={4}>
                All API requests must be authenticated using a Bearer token obtained from the `/oauth/token` endpoint.
                The token should be included in the `Authorization` header of each request.
              </Text>
              <Code p={2} borderRadius="md" bg={codeBg} display="block" whiteSpace="pre">
                Authorization: Bearer YOUR_ACCESS_TOKEN
              </Code>
            </Box>

            {/* Workflow Section */}
            <Box id="workflow" mb={10}>
              <Heading as="h2" size="lg" mb={4}>Synchronous Coding Workflow</Heading>
              <Text mb={4}>
                The process is designed to be simple and sequential. You only need to keep track of your own unique case identifier (`external_case_id`) throughout the process.
              </Text>
              <OrderedList spacing={3}>
                <ListItem>
                  <Text><b>Create a Case:</b> Start by creating a case container, providing your own unique ID for the case.</Text>
                </ListItem>
                <ListItem>
                  <Text><b>Assign Carrier (Optional):</b> If needed, associate a payer and state with the case for more specific analysis.</Text>
                </ListItem>
                <ListItem>
                  <Text><b>Upload Content:</b> Add one or more medical documents (text, PDF, etc.) to the case.</Text>
                </ListItem>
                <ListItem>
                  <Text><b>Request Coding:</b> Make separate calls to get CPT and ICD-10 codes. The API will process the content and return the results directly in the response.</Text>
                </ListItem>
              </OrderedList>
            </Box>

            {/* Endpoints Section */}
            <Box mb={10}>
              <Heading as="h2" size="lg" mb={6}>Endpoints</Heading>

              <EndpointSection
                id="create-case"
                title="1. Create Case"
                method="POST"
                endpoint="/create_external_case"
                description="Creates a new case to act as a container for a single coding job. You must provide your own unique identifier for the case."
                headers="Authorization: Bearer YOUR_ACCESS_TOKEN\nContent-Type: application/json"
                requestBody={`{
  "external_case_id": "YOUR-UNIQUE-ID-123", // Required. Your internal identifier for this case.
  "case_type": "E/M", // Optional. Helps select the best processing model.
  "client_id": 42 // Optional. Associates the case with a pre-configured client.
}`}
                responses={[
                  {
                    status: 201,
                    description: "Created - Case created successfully",
                    example: `{
  "message": "External case created successfully",
  "case_id": "b1a4ce7f-a9e9-4f13-8df0-66a221d57b57"
}`
                  },
                  {
                    status: 400,
                    description: "Bad Request - Missing required external_case_id",
                    example: `{
  "error": "External case ID required"
}`
                  }
                ]}
                example={`curl --request POST \\
  --url 'https://api.revedy.io/create_external_case' \\
  --header 'Authorization: Bearer YOUR_ACCESS_TOKEN' \\
  --header 'Content-Type: application/json' \\
  --data '{
    "external_case_id": "YOUR-UNIQUE-ID-123",
    "case_type": "E/M"
  }'`}
              />

              <EndpointSection
                id="assign-carrier"
                title="2. Assign Carrier (Optional)"
                method="POST"
                endpoint="/assign_carrier"
                description="Associates a payer (insurance carrier) and state with the case. This is useful for medical necessity and reimbursement analysis."
                headers="Authorization: Bearer YOUR_ACCESS_TOKEN\nContent-Type: application/json"
                requestBody={`{
  "case_id": "YOUR-UNIQUE-ID-123", // Required. Must be your external_case_id.
  "payer_type": "Aetna", // Optional. The name of the payer.
  "carrier_name": "Aetna", // Optional. Typically the same as payer_type.
  "state_full": "Texas" // Optional. The full name of the state.
}`}
                responses={[
                  {
                    status: 200,
                    description: "OK - Carrier assigned successfully",
                    example: `{
  "message": "Carrier assigned successfully"
}`
                  },
                  {
                    status: 404,
                    description: "Not Found - The specified case_id does not exist",
                    example: `{
  "error": "Case not found"
}`
                  }
                ]}
                example={`curl --request POST \\
  --url 'https://api.revedy.io/assign_carrier' \\
  --header 'Authorization: Bearer YOUR_ACCESS_TOKEN' \\
  --header 'Content-Type: application/json' \\
  --data '{
    "case_id": "YOUR-UNIQUE-ID-123",
    "payer_type": "Aetna",
    "state_full": "Texas"
  }'`}
              />

              <EndpointSection
                id="upload-file"
                title="3. Upload Content"
                method="POST"
                endpoint="/upload_file/{external_case_id}"
                description="Uploads a file (e.g., PDF, TXT, JPG) to an existing case. You can call this endpoint multiple times to add several documents to the same case."
                headers="Authorization: Bearer YOUR_ACCESS_TOKEN\nContent-Type: multipart/form-data"
                pathParams={[
                  {
                    name: "external_case_id",
                    description: "Your unique identifier for the case.",
                    required: true
                  }
                ]}
                requestBody={`file: The file content to upload.
file_type_id: The type of file. Use '5' for a standard clinical note.`}
                responses={[
                  {
                    status: 201,
                    description: "Created - File uploaded successfully",
                    example: `{
  "message": "File uploaded successfully",
  "file_id": 123
}`
                  },
                  {
                    status: 404,
                    description: "Not Found - The specified external_case_id does not exist",
                    example: `{
  "error": "Case not found"
}`
                  }
                ]}
                example={`curl --request POST \\
  --url 'https://api.revedy.io/upload_file/YOUR-UNIQUE-ID-123' \\
  --header 'Authorization: Bearer YOUR_ACCESS_TOKEN' \\
  --header 'Content-Type: multipart/form-data' \\
  --form 'file=@/path/to/your/note.pdf' \\
  --form 'file_type_id=5'`}
              />

              <EndpointSection
                id="cpt-coding"
                title="4. Get CPT Codes"
                method="POST"
                endpoint="/cpt_coding"
                description="Processes all content within a case to generate CPT codes. Set 'background' to false to receive results directly in the response."
                headers="Authorization: Bearer YOUR_ACCESS_TOKEN\nContent-Type: application/json"
                requestBody={`{
  "external_case_id": "YOUR-UNIQUE-ID-123", // Required. Your case identifier.
  "background": false // Required. Must be false for synchronous response.
}`}
                responses={[
                  {
                    status: 200,
                    description: "OK - CPT coding results returned successfully",
                    example: `{
  "status": "success",
  "results": {
    "99213": {
      "billable": true,
      "cpt_code": "99213",
      "justification": "E/M code 99213 selected based on...",
      "long_description": "Office or other outpatient visit...",
      "units": 1,
      "work_rvu": 0.97
    }
  }
}`
                  },
                  {
                    status: 404,
                    description: "Not Found - The specified external_case_id does not exist",
                    example: `{
  "error": "Case not found"
}`
                  }
                ]}
                example={`curl --request POST \\
  --url 'https://api.revedy.io/cpt_coding' \\
  --header 'Authorization: Bearer YOUR_ACCESS_TOKEN' \\
  --header 'Content-Type: application/json' \\
  --data '{
    "external_case_id": "YOUR-UNIQUE-ID-123",
    "background": false
  }'`}
              />

              <EndpointSection
                id="icd10-coding"
                title="5. Get ICD-10 Codes"
                method="POST"
                endpoint="/icd10_coding"
                description="Processes all content within a case to generate ICD-10 codes. Set 'background' to false to receive results directly in the response."
                headers="Authorization: Bearer YOUR_ACCESS_TOKEN\nContent-Type: application/json"
                requestBody={`{
  "external_case_id": "YOUR-UNIQUE-ID-123", // Required. Your case identifier.
  "background": false // Required. Must be false for synchronous response.
}`}
                responses={[
                  {
                    status: 200,
                    description: "OK - ICD-10 coding results returned successfully",
                    example: `{
  "status": "success",
  "results": {
    "M54.5": {
      "description": "Low back pain",
      "evidence": "Patient reports chronic lower back pain...",
      "is_primary": true,
      "justification": "The documentation clearly indicates the patient has low back pain..."
    }
  }
}`
                  },
                  {
                    status: 404,
                    description: "Not Found - The specified external_case_id does not exist",
                    example: `{
  "error": "Case not found"
}`
                  }
                ]}
                example={`curl --request POST \\
  --url 'https://api.revedy.io/icd10_coding' \\
  --header 'Authorization: Bearer YOUR_ACCESS_TOKEN' \\
  --header 'Content-Type: application/json' \\
  --data '{
    "external_case_id": "YOUR-UNIQUE-ID-123",
    "background": false
  }'`}
              />
            </Box>

            {/* Advanced Section */}
            <Box id="async-processing" mb={10}>
              <Heading as="h2" size="lg" mb={4}>Advanced: Asynchronous Processing</Heading>
              <Alert status="info" variant="left-accent" bg={useColorModeValue("blue.50", "blue.900")}>
                <AlertIcon />
                <Box>
                  <AlertTitle>For User Interface Integrations</AlertTitle>
                  <AlertDescription>
                    <Text>
                      While the synchronous workflow is best for server-to-server tasks, it is not recommended for user-facing applications, as the coding process can take several seconds and would lock up the UI.
                    </Text>
                    <Text mt={2}>
                      For these scenarios, an asynchronous mode is available. To use it, set `"background": true` in your `/cpt_coding` or `/icd10_coding` request and include a `socket_id`. The API will respond immediately with a `202 Accepted` status, and the results will be delivered later via a WebSocket event.
                    </Text>
                  </AlertDescription>
                </Box>
              </Alert>
            </Box>

          </Box>
        </GridItem>

        {/* Footer */}
        <GridItem area="footer" bg={bgColor} borderTop="1px" borderColor={borderColor} p={4} textAlign="center">
          <Text fontSize="sm">© {new Date().getFullYear()} Revedy. All rights reserved.</Text>
        </GridItem>
      </Grid>
    </Box>
  );
}
export default FullCPTCoderAPIDocs;